import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    type CommonQueryArgs,
    DAAS_ACTIVE_LISTINGS,
    DAAS_LISTINGS_BY_SOURCE,
    getQueryString,
    useQueryDisabled,
} from '@pkg/hooks';

import { type ListingQueryParams } from '@app/client';
import { callGetActiveListingData } from '@app/client/resources/daas';
import { callGetListings, type UnifiedListingsFormat } from '@app/client/resources/daas/v2/market/listing';

export interface GetListingDataArgs extends CommonQueryArgs, ListingQueryParams {
    tokenIds?: string[];
    limit?: number;
    listings?: UnifiedListingsFormat[];
}

export function useGetActiveListingData({ tokenIds, slugs, limit = 1, disabled = false }: GetListingDataArgs) {
    const queryClient = useQueryClient();

    const query = {
        wei_price_lower_bound_inclusive: true,
        wei_price_upper_bound_inclusive: true,
        count_only: false,
        limit,
    };
    const queryArrayKey = tokenIds?.length ? 'token_ids' : slugs?.length ? 'slugs' : undefined;
    const queryArray = tokenIds?.length ? tokenIds : slugs?.length ? slugs : undefined;

    const queryString = getQueryString({
        queryArrayKey,
        queryArray,
        queryParams: query,
    });

    const cache = [DAAS_ACTIVE_LISTINGS, queryString, 'data'];
    const hasQueryData = cache && queryClient.getQueryData(cache);
    const queryDisabled = useQueryDisabled([!disabled, !hasQueryData, tokenIds || slugs]);

    return useQuery({
        queryKey: cache,
        queryFn: ({ signal }) =>
            callGetActiveListingData({
                settings: { signal },
                customQuery: `${queryString}`,
            }),
        retry: false,
        enabled: !queryDisabled,
        placeholderData: keepPreviousData,
        select: (res) => {
            if (res?.ok && res?.value) {
                return res.value;
            }
        },
    });
}

export function useGetListingsBySource({ listings, disabled = false }: GetListingDataArgs) {
    const queryClient = useQueryClient();
    const queryString = getQueryString({
        queryArrayKey: 'listings',
        queryArray: listings,
    });

    const cache = [DAAS_LISTINGS_BY_SOURCE, queryString, 'listing'];
    const hasQueryData = cache && queryClient.getQueryData(cache);
    const queryDisabled = useQueryDisabled([!disabled, !hasQueryData, listings?.length]);

    return useQuery({
        queryKey: cache,
        queryFn: ({ signal }) =>
            callGetListings({
                settings: { signal },
                customQuery: queryString,
            }),
        retry: false,
        enabled: !queryDisabled,
        placeholderData: keepPreviousData,
        select: (res) => {
            if (res?.ok && res?.value) {
                return res.value;
            }
        },
    });
}
